import along from "@turf/along";
import area from "@turf/area";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import buffer from "@turf/buffer";
import { convex } from "@turf/convex";
import { feature as asFeature, featureCollection } from "@turf/helpers";
import intersect from "@turf/intersect";
import length from "@turf/length";
import { lineSplit } from "@turf/line-split";
import { pointToLineDistance } from "@turf/point-to-line-distance";
import union from "@turf/union";
import type { LineString, MultiLineString, MultiPolygon, Polygon } from "geojson";

import {
  areaCentroid,
  DEG_TO_RAD,
  holeCount,
  orientedRectangleSidesMetres,
  partCount,
  pointCount,
  polygonPerimeterMetres,
  toLineStrings,
} from "./geometry";

export type StreetGeometry = LineString | MultiLineString;

export interface ParcelInput {
  /** The geometry of the parcel. **/
  geometry: Polygon | MultiPolygon;

  /** The `AddressType` of the parcel. **/
  addressType: string | null;

  /** Streets near the parcel (excluding service streets) **/
  gateStreets: ReadonlyArray<StreetGeometry>;

  /** All streets near the parcel **/
  nearbyStreets: ReadonlyArray<StreetGeometry>;
}

export interface StreetParcelConfig {
  streetBufferDistanceMetres: number;
}

export const DEFAULT_STREET_PARCEL_CONFIG: StreetParcelConfig = {
  streetBufferDistanceMetres: 10,
};

/**
 * The 'features' of a street parcel that the model considers.
 *
 * The terminology here is somewhat confusing: 'feature' in this context does not mean "geospatial feature";
 * rather, it just means "some attribute or derived value obtained from a parcel".
 */
export interface StreetParcelFeatures {
  // The attributes must align with the features in the model; the model was trained using snake_case,
  // so we align with that naming here.

  /** The area of the parcel. **/
  area_m2: number;

  /**
   * The ratio of the area of buffered streets near the parcel to the area of the parcel itself.
   *
   * This is by far the most powerful feature in terms of detecting street parcels.
   * **/
  street_overlap_ratio: number | null;

  /** The perimeter of the parcel. **/
  perimeter_m: number;

  /**
   * The compactness of the parcel.
   *
   * This is measured using the 'Polsby-Popper' score. Scores range from 0 to 1, with 1 being a perfect circle.
   **/
  compactness: number | null;

  /**
   * The elongation of the parce.
   *
   * This is measured by the ratio: (max side length of OBB)/(min side length of OBB),
   * where OBB is the oriented bounding box of the parcel.
   */
  elongation: number | null;

  /**
   * The width of the parcel.
   *
   * This is measured by the smallest side length of the oriented bounding box of the parcel.
   */
  width_m: number | null;

  /**
   * The convexity of the parcel.
   *
   * This is measured by the ratio of the parcel's area to the area of the parcel's convex hull.
   */
  convexity: number | null;

  /**
   * The number of vertices in the parcel.
   */
  n_points: number;

  /**
   * The number of parts in the parcel.
   *
   * A number greater than one indicates that the parcel's geometry is a MultiPolygon.
   */
  n_parts: number;

  /**
   * The number of 'holes' in the parcel.
   */
  hole_count: number;

  /**
   * The total length of street centerlines within the parcel.
   *
   * Only the portion of the street centerlines contained within the parcel contribute to the length.
   */
  centerline_len_m: number;

  /**
   * The ratio of the street centerline lengths to the perimeter of the parcel.
   */
  centerline_len_per_perim: number | null;

  /**
   * The distance from the parcel centroid to the nearest street, normalized by the square root of the parcel's area
   */
  centroid_street_dist_norm: number | null;

  /** The address type of the parcel. **/
  addresstype: string;
}

/** Return the convexity of the given geometry. **/
function convexity(geom: Polygon | MultiPolygon, areaM2: number): number | null {
  const hull = convex(geom);
  if (!hull) return null;
  const hullArea = area(hull);
  return hullArea > 0 ? areaM2 / hullArea : null;
}

/**
 * Calculate the street overlap ratio of the given parcel geometry.
 *
 * The rough idea is that we buffer each nearby street, union those buffers, intersect the buffer
 * with the parcel, and then measure the area of that intersection. This provides a measure
 * of 'how much of the parcel is close to a street'.
 */
function streetOverlapRatio(
  geom: Polygon | MultiPolygon,
  gateStreets: ReadonlyArray<StreetGeometry>,
  parcelArea: number,
  latitude: number,
  config: StreetParcelConfig
): number | null {
  if (parcelArea <= 0) return null;

  // the model was trained using a 10 "metre" buffer in Web Mercator, so we replicate that buffer here
  const metres = config.streetBufferDistanceMetres * Math.cos(latitude * DEG_TO_RAD);
  const buffered = gateStreets.map((street) => buffer(street, metres, { units: "meters" })).filter(Boolean);
  if (buffered.length === 0) return 0;

  // union throws if the input has length 1
  const merged = buffered.length === 1 ? buffered[0] : union(featureCollection(buffered));
  if (!merged) return 0;

  const overlap = intersect(featureCollection([asFeature(geom), merged]));
  return Math.min((overlap ? area(overlap) : 0) / parcelArea, 1);
}

/**
 * Distance from the parcel centroid to the nearest street, normalised by `sqrt(area)`.
 */
function centroidStreetDistNorm(geom: Polygon | MultiPolygon, nearbyStreets: ReadonlyArray<StreetGeometry>, areaM2: number): number | null {
  if (areaM2 <= 0) return null;
  const centroid = areaCentroid(geom);
  if (!centroid) return null;

  let nearest = Infinity;
  for (const street of nearbyStreets) {
    for (const line of toLineStrings(street)) {
      const distance = pointToLineDistance(centroid, line, { method: "geodesic", units: "meters" });
      if (distance < nearest) nearest = distance;
    }
  }
  return nearest === Infinity ? null : nearest / Math.sqrt(areaM2);
}

/** Total length of street centrelines inside the parcel. */
function centerlineLengthMetres(geom: Polygon | MultiPolygon, nearbyStreets: ReadonlyArray<StreetGeometry>): number {
  // ideally we could simply intersect each street with the parcel geometry and then compute the sum of the lengths
  // of the resulting (multi)line strings, however turf doesn't appear to support this type of intersection.
  //
  // instead, we split each linestring where it intersects the parcel geometry. for each segment we then classify
  // whether that segment is "inside" or "outside" the polygon; the "inside" segments contribute to the total
  // length, while the "outside" segments are ignored.

  const parcel = asFeature(geom);
  let total = 0;

  for (const street of nearbyStreets) {
    for (const line of toLineStrings(street)) {
      const lineFeature = asFeature(line);
      let segments;

      // lineSplit is somewhat unreliable; if it fails we assume a single segment.
      try {
        const split = lineSplit(lineFeature, parcel);
        segments = split.features.length > 0 ? split.features : [lineFeature];
      } catch {
        segments = [lineFeature];
      }
      for (const segment of segments) {
        const segmentLengthM = length(segment, { units: "meters" });
        if (segmentLengthM > 0) {
          const interior = along(segment, segmentLengthM / 2, { units: "meters" });
          if (booleanPointInPolygon(interior, geom)) total += segmentLengthM;
        }
      }
    }
  }
  return total;
}

/** Compute the model features for a given parcel. **/
export function computeStreetParcelFeatures(parcel: ParcelInput, config: StreetParcelConfig = DEFAULT_STREET_PARCEL_CONFIG): StreetParcelFeatures {
  const { geometry } = parcel;
  const areaM2 = area(geometry);
  const perimeterM = polygonPerimeterMetres(geometry);
  const sides = orientedRectangleSidesMetres(geometry);
  const centroid = areaCentroid(geometry);
  const latitude = centroid ? centroid.coordinates[1] : 0;
  const centerlineLen = centerlineLengthMetres(geometry, parcel.nearbyStreets);

  return {
    area_m2: areaM2,
    street_overlap_ratio: streetOverlapRatio(geometry, parcel.gateStreets, areaM2, latitude, config),
    perimeter_m: perimeterM,
    compactness: perimeterM > 0 ? (4 * Math.PI * areaM2) / perimeterM ** 2 : null,
    elongation: sides ? Math.max(...sides) / Math.min(...sides) : null,
    width_m: sides ? Math.min(...sides) : null,
    convexity: convexity(geometry, areaM2),
    n_points: pointCount(geometry),
    n_parts: partCount(geometry),
    hole_count: holeCount(geometry),
    centerline_len_m: centerlineLen,
    centerline_len_per_perim: perimeterM > 0 ? centerlineLen / perimeterM : null,
    centroid_street_dist_norm: centroidStreetDistNorm(geometry, parcel.nearbyStreets, areaM2),
    addresstype: parcel.addressType ?? "",
  };
}
